import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {FormattedMessage, useIntl} from 'react-intl';

import {IStore} from 'redux/interface';
import {useNavigate} from 'react-router-dom';
import {ChatroomConsentModalWindow} from '../../components/chatroom-consent-modal-window/ChatroomConsentModalWindow';
import {updateUserNickAction} from '../../redux/auth/authAsyncActions';
import {ButtonVariant, GritxButton, IUserProfile} from '@wholesalechange/chatcomponent';
import {
  addDialogueToFavorites,
  chatroomAsyncActions,
  createChatroomDialogue,
  getChatroomDialogues,
  getTranslations, removeDialogueFromFavorites
} from '../../redux/pageAsyncActions';
import {ReactComponent as ChatIcon} from '../../assets/image/menu-auth/chat.svg';
import InputRadio from '../../components/input-radio';
import Loader from '../../components/loader';
import NotFoundInfo from '../../components/not-found-info';
import NotFoundImg from '../../assets/image/common/notFound.png';
import ChatroomCardSection from '../../components/chatroom-card-section';
import {IChatroomDialogue, INewPost} from '../../redux/chatroom/interfaces';
import {IRadioInput} from '../../components/input-radio/interfaces';
import {ChatroomModalWindow} from '../../components/chatroom-modal-window/ChatroomModalWindow';
import {dialogueClearedAction} from '../../redux/dialogue/actions';
import {useGetTranslations} from '../../utils/hooks/useGetTranslations';
import {Pages, TranslationKeys} from '../../utils/constants/routeConstants';

import './styles.scss';

export const Chatroom: React.FunctionComponent = () => {
  const {
    dialogues
  } = useSelector((state: IStore) => state.chatroom);
  const {
    loading
  } = useSelector((state: IStore) => state.status);
  const {
    userProfile, settings
  } = useSelector((state: IStore) => state.auth);
  const intl = useIntl();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isShowConsentDialogue, setIsShowConsentDialogue] = useState<boolean>(false);
  const [isShowCreatePost, setIsShowCreatePost] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('all');
  const [filteredCards, setFilteredCards] = useState<IChatroomDialogue[]>([]);
  const [newPost, setNewPost] = useState<INewPost>({
    name: '',
    text: ''
  });

  const translationKey = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Chatroom.toLowerCase());

    return page?.translationKey || TranslationKeys.Chatroom;
  }, [settings]);

  useGetTranslations({translationKey});

  const title = useMemo(() => {
    const page = settings?.pages.find((p) => p.name.toLowerCase() === Pages.Chatroom.toLowerCase());

    return page?.title || Pages.Chatroom;
  }, [settings]);

  const filterRadioInputs: IRadioInput[] = useMemo(() => {
    if (loading.includes(getTranslations)) return [];

    return [
      {
        id: 0,
        name: 'my',
        label: intl.formatMessage({id: 'gritx.chatroom.filter.my', defaultMessage: 'My posts'}),
        value: 'my'
      },
      {
        id: 1,
        name: 'favorites',
        label: intl.formatMessage({id: 'gritx.chatroom.filter.favorites', defaultMessage: 'Favorites'}),
        value: 'favorites'
      },
      {
        id: 2,
        name: 'all',
        label: intl.formatMessage({id: 'gritx.chatroom.filter.all', defaultMessage: 'All posts'}),
        value: 'all'
      }
    ];
  }, [loading]);

  useEffect(() => {
    dispatch(chatroomAsyncActions[getChatroomDialogues]());
    dispatch(dialogueClearedAction());
  }, []);

  function getFilteredCards(filterType: string) {
    const copy = dialogues.slice(0);

    if (filterType === 'my') {
      return copy.filter(c => c.userId === userProfile?.id);
    }
    if (filterType === 'favorites') {
      return copy.filter(c => c.favourite);
    }

    return copy;
  }

  useEffect(() => {
    if (dialogues) {
      setFilteredCards(getFilteredCards(filter));
    }
  }, [dialogues, filter]);

  useEffect(() => {
    if (userProfile && !userProfile.nickname) {
      setIsShowConsentDialogue(true);
    }
  }, [userProfile]);

  function handleSaveConsentDialogue(nickname: string) {
    setIsShowConsentDialogue(false);
    dispatch(updateUserNickAction({
      ...userProfile,
      nickname
    } as IUserProfile));
  }

  function handleCloseConsentDialogue() {
    setIsShowConsentDialogue(false);
  }

  function handleClickNickname() {
    setIsShowConsentDialogue(true);
  }

  function handleOpenDialogue(dialogue: IChatroomDialogue) {
    if (userProfile && !userProfile.nickname) {
      setIsShowConsentDialogue(true);

      return;
    }
    navigate(`/chatroom/${dialogue.id}`);
  }

  function handleChangeFavorite(dialogue: IChatroomDialogue) {
    if (dialogue.favourite) {
      dispatch(chatroomAsyncActions[removeDialogueFromFavorites](dialogue));
    } else {
      dispatch(chatroomAsyncActions[addDialogueToFavorites](dialogue));
    }
  }

  function onCreateChatroomDialogue() {
    if (userProfile && !userProfile.nickname) {
      setIsShowConsentDialogue(true);

      return;
    }
    setIsShowCreatePost(true);
  }

  function handleChangeFilter(value: string) {
    setFilter(value);
  }

  function handleCreatePost(post: INewPost) {
    dispatch(chatroomAsyncActions[createChatroomDialogue](post));
    setNewPost({
      name: '',
      text: ''
    });
    setIsShowCreatePost(false);
  }

  function handleCloseChatroomDialogue() {
    setNewPost({
      name: '',
      text: ''
    });
    setIsShowCreatePost(false);
  }

  function renderChatroomCards() {
    return <div className="chatroom__cards">
      {filteredCards.length === 0 ? <NotFoundInfo
        title={intl.formatMessage({
          id: 'gritx.chatroom.sectionCards.notFound',
          defaultMessage: 'Sorry, but nothing can be found.'
        })}
        image={NotFoundImg}
      /> : <ChatroomCardSection
        showLoader={loading.includes(getChatroomDialogues)}
        cards={filteredCards}
        onClickCard={handleOpenDialogue}
        onChangeFavorite={handleChangeFavorite}
      />}
    </div>;
  }

  return <section className={'chatroom chatroom__container'}>
    {
      !loading.includes(getTranslations) && <>
        <div className="chatroom__title-wrapper">
          <h1 className="chatroom__title">
            {/* <ChatIcon className="chatroom__icon"/>*/}
            <FormattedMessage
              id={`gritx.common.navigation.${title}`}
              defaultMessage={title}
            />
          </h1>
          <button className="chatroom__nickname" onClick={handleClickNickname}>{userProfile?.nickname}</button>
        </div>
        <div className="chatroom__header">
          <div className="chatroom__header-sort">
            <InputRadio inputs={filterRadioInputs} value={filter} onChange={handleChangeFilter} label={intl.formatMessage({
              id: 'gritx.chatroom.filter.title',
              defaultMessage: 'Filter by:'
            })}/>
          </div>
          <GritxButton
            title={intl.formatMessage({
              id: 'gritx.chatroom.create',
              defaultMessage: 'Create'
            })}
            className={'chatroom__button'}
            variant={ButtonVariant.Primary}
            onClick={onCreateChatroomDialogue}
          />
        </div>
        {
          loading.includes(getChatroomDialogues) || loading.includes(createChatroomDialogue) ? <Loader/> : renderChatroomCards()
        }
        <ChatroomConsentModalWindow
          title={intl.formatMessage({
            id: 'gritx.chatroom.consent.title',
            defaultMessage: 'Enter Your Nickname'
          })}
          onSave={handleSaveConsentDialogue}
          onClose={handleCloseConsentDialogue}
          show={isShowConsentDialogue}
          profileNickname={userProfile?.nickname}
        />
        <ChatroomModalWindow
          card={newPost}
          title={intl.formatMessage({
            id: 'gritx.chatroom.create.title',
            defaultMessage: 'Create a new Post'
          })}
          onSave={handleCreatePost}
          onClose={handleCloseChatroomDialogue}
          show={isShowCreatePost}
        />
      </>
    }
  </section>;
};
